import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  Heading,
  AlertDialogBody,
  AlertDialogFooter,
  ModalFooterProps,
  ModalBodyProps,
  ComponentWithAs,
  IconProps,
  Grid,
  Box,
} from '@chakra-ui/react'
import { HTMLChakraProps } from '@chakra-ui/system'

import styled from '@emotion/styled'
import React, { FC, useContext, useRef, RefObject } from 'react'
import { Info } from '../../icons'
import { TuseColorArgs, useColor } from '../../theme'
import { IButtonProps, Button } from '../Button/Button'

interface IDialogProps {
  isOpen: boolean
  onClose: () => void
  maxWidth?: string
  children?: React.ReactNode
}

interface IIconProps {
  icon?: ComponentWithAs<'svg', IconProps>
  color?: TuseColorArgs
  isRotated?: boolean
}

interface IModalHeaderProps extends HTMLChakraProps<'header'> {
  icon?: ComponentWithAs<'svg', IconProps>
  headerColor?: TuseColorArgs
  showIcon?: boolean
}

const DialogContext = React.createContext<{
  onClose: () => void
  closeButtonRef: RefObject<HTMLButtonElement> | undefined
}>({
  onClose: () => undefined,
  closeButtonRef: undefined,
})
const Root: React.FC<IDialogProps> = ({
  children,
  isOpen,
  onClose,
  maxWidth,
}) => {
  const closeButtonRef = useRef(null)

  return (
    <DialogContext.Provider value={{ onClose, closeButtonRef }}>
      <AlertDialog
        closeOnOverlayClick={false}
        isCentered
        isOpen={isOpen}
        leastDestructiveRef={closeButtonRef}
        onClose={onClose}>
        <AlertDialogOverlay />
        <AlertDialogContent
          data-testid="dialog-root-content"
          display="flex"
          flexDir="column"
          gridGap={8}
          maxW={maxWidth ?? 'md'}
          p={8}>
          {children}
        </AlertDialogContent>
      </AlertDialog>
    </DialogContext.Provider>
  )
}

const Icon: React.FC<IIconProps> = ({ icon, color, isRotated }) => {
  const Component = icon ?? Info
  const givenColor = useColor(color ?? { nuance: 4, palette: 'orange' })

  return (
    <Component
      boxSize={6}
      color={givenColor}
      mr="18px"
      transform={isRotated ? 'rotate(180deg)' : 'rotate(0deg)'}
    />
  )
}

const Header: React.FC<IModalHeaderProps> = ({ ...props }) => {
  return (
    <Box display="flex">
      <Heading
        align="center"
        display="flex"
        fontSize="2xl"
        fontWeight="bold"
        p={0}
        width="full"
        {...props}
      />
    </Box>
  )
}

const Body: React.FC<ModalBodyProps> = (props) => {
  const gray = useColor({ nuance: 6, palette: 'secondary' })

  return (
    <AlertDialogBody
      color={gray}
      data-testid="dialog-body"
      fontSize="md"
      fontWeight="medium"
      p={0}
      {...props}
    />
  )
}

// last button in the footer takes full width if there are either 3 or 1 item
const Layout = styled(Grid)`
  & > *:nth-child(odd):last-child {
    grid-column: 1 / -1;
  }
`

const Footer: React.FC<ModalFooterProps> = ({ children, ...props }) => (
  <AlertDialogFooter p={0} {...props}>
    <Layout gap={6} templateColumns="repeat(2, 1fr)" w="full">
      {children}
    </Layout>
  </AlertDialogFooter>
)

const Action: FC<IButtonProps> = (props) => (
  <Button
    data-testid="action-button"
    fontSize="md"
    size="lg"
    variant={props.variant ? props.variant : 'primary'}
    {...props}
  />
)

const Close: FC<IButtonProps> = (props) => {
  const { onClose, closeButtonRef } = useContext(DialogContext)

  return (
    <Button
      data-testid="close-action-button"
      fontSize="md"
      onClick={onClose}
      ref={closeButtonRef}
      size="lg"
      variant={props.variant ? props.variant : 'secondary'}
      width="full"
      {...props}
    />
  )
}

const Cancel: FC<IButtonProps> = (props) => {
  const { onClose } = useContext(DialogContext)

  return (
    <Button
      data-testid="cancel-action-button"
      fontSize="md"
      onClick={onClose}
      size="lg"
      variant="secondary"
      {...props}
    />
  )
}

export const Dialog = {
  Icon,
  Root: Root,
  Header,
  Body,
  Footer,
  Action,
  Cancel,
  Close,
}
